import React, { useEffect, useMemo, useCallback, useState } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  FormHelperText,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useForm, Controller } from 'react-hook-form';
import { useDataProvider } from 'react-admin';
import {
  SubdivisionDto,
  RegionDto,
  BuilderInterface,
  PhaseStructureTypeEnum,
  SubdivisionWarrantyStatusEnum,
  SubdivisionOverrideTypeEnum,
} from '@vatos-pas/common';
import { states } from 'components/State/states';

import {
  FormToolbar,
  Autocomplete,
  SnackbarContainer,
} from 'components/standalone';
import { useRefreshTrigger } from 'hooks';
import { createValidationResolver, normalizeFormNumber } from 'utils';
import {
  SubdivisionValidationContext,
  validateSubdivisionCreation,
} from '../views/subDivisionValidation';
import { SubDivisionCostStructureFields } from './SubDivisionCostStructureFields';
import { SubDivisionSupervisorsSection } from './SubDivisionSupervisorsSection';
import { BumpoutTimesheetField } from './BumpoutTimesheetField';
import { WarrantyStatusChoices } from '../constants';
import {
  SubDivisionFormValues,
  SupervisorOption,
  UserRoleRegion,
} from '../containers/useSubDivisionContainer';

interface SubDivisionFormProps {
  mode: 'create' | 'edit';
  initialValues?: SubdivisionDto | null;
  onSubmit: (values: SubDivisionFormValues) => Promise<void>;
  loading: boolean;
  onGoBack: () => void;
  // Supervisor data (only used in edit mode)
  supervisors?: UserRoleRegion[];
  builderSupervisors?: UserRoleRegion[];
  supervisorsLoading?: boolean;
  // Selected supervisor values (managed externally for batch update, edit mode only)
  selectedSupervisors?: SupervisorOption[];
  selectedBuilderSupervisors?: SupervisorOption[];
  onSupervisorsChange?: (supervisors: SupervisorOption[]) => void;
  onBuilderSupervisorsChange?: (supervisors: SupervisorOption[]) => void;
  supervisorError?: string;
  // Region data for cost structure
  selectedRegion: RegionDto | null;
  regionLoading: boolean;
  onRegionChange: (regionId: string) => void;
  // Permissions
  canToggleBumpoutTimesheet: boolean;
  canViewGeofencingThreshold: boolean;
  canEditGeofencingThreshold: boolean;
}

const getDefaultValues = (
  initialValues?: SubdivisionDto | null,
  regionPhaseStructureType?: string,
  mode: 'create' | 'edit' = 'create',
): SubDivisionFormValues => ({
  name: initialValues?.name || '',
  address1: initialValues?.address1 || '',
  address2: initialValues?.address2 || '',
  city: initialValues?.city || '',
  state: initialValues?.state || '',
  zip: initialValues?.zip || '',
  builderId: initialValues?.builderId || '',
  regionId: initialValues?.regionId || '',
  active: initialValues?.active ?? true,
  warrantyStatus:
    initialValues?.warrantyStatus || SubdivisionWarrantyStatusEnum.PRODUCTION,
  reqBumpoutTimesheet: initialValues?.reqBumpoutTimesheet ?? true,
  phaseStructureType:
    initialValues?.phaseStructureType ||
    regionPhaseStructureType ||
    PhaseStructureTypeEnum.Drywall,
  overrideRegionCostPerBoard:
    initialValues?.overrideRegionCostPerBoard ?? false,
  overrideType:
    initialValues?.overrideType || SubdivisionOverrideTypeEnum.OFFSET,
  // Drywall fields
  dwHangingCostPerBoard: initialValues?.dwHangingCostPerBoard ?? undefined,
  dwFinishingCostPerBoard: initialValues?.dwFinishingCostPerBoard ?? undefined,
  dwSprayingCostPerBoard: initialValues?.dwSprayingCostPerBoard ?? undefined,
  // DrywallAndPaint fields
  dwpHangingCostPerBoard: initialValues?.dwpHangingCostPerBoard ?? undefined,
  dwpFinishCostPerBoard: initialValues?.dwpFinishCostPerBoard ?? undefined,
  dwpPrimeCostPerSqft: initialValues?.dwpPrimeCostPerSqft ?? undefined,
  dwpTrim1CostPerSqft: initialValues?.dwpTrim1CostPerSqft ?? undefined,
  dwpBump1CostPerBoard: initialValues?.dwpBump1CostPerBoard ?? undefined,
  dwpInteriorPaintCostPerSqft:
    initialValues?.dwpInteriorPaintCostPerSqft ?? undefined,
  dwpBump2CostPerBoard: initialValues?.dwpBump2CostPerBoard ?? undefined,
  dwpTrim2CostPerSqft: initialValues?.dwpTrim2CostPerSqft ?? undefined,
  dwpFinalPaintCostPerSqft:
    initialValues?.dwpFinalPaintCostPerSqft ?? undefined,
  dwpBump3CostPerBoard: initialValues?.dwpBump3CostPerBoard ?? undefined,
  dwpCMBluetapeCostPerSqft:
    initialValues?.dwpCMBluetapeCostPerSqft ?? undefined,
  dwpFinalBluetapeCostPerSqft:
    initialValues?.dwpFinalBluetapeCostPerSqft ?? undefined,
  exteriorOnePercentage: normalizeFormNumber(
    initialValues?.exteriorOnePercentage,
  ),
  exteriorTwoPercentage: normalizeFormNumber(
    initialValues?.exteriorTwoPercentage,
  ),
  // Common cost fields
  pieceworkPercentage: normalizeFormNumber(initialValues?.pieceworkPercentage),
  specialJobsPercentage: normalizeFormNumber(
    initialValues?.specialJobsPercentage,
  ),
  garageCostFixed: initialValues?.garageCostFixed ?? undefined,
  highCeilingPerBoard: initialValues?.highCeilingPerBoard ?? undefined,
  screwInspectionPerBoard: initialValues?.screwInspectionPerBoard ?? undefined,
  interiorOptionsPercentage: normalizeFormNumber(
    initialValues?.interiorOptionsPercentage,
  ),
  exteriorOptionsPercentage: normalizeFormNumber(
    initialValues?.exteriorOptionsPercentage,
  ),
  trimOptionPercentage: normalizeFormNumber(
    initialValues?.trimOptionPercentage,
  ),
  geoDistanceTolerance:
    initialValues?.geoDistanceTolerance ?? (mode === 'create' ? 350 : null),
  allowPayoutAmountEditing: initialValues?.allowPayoutAmountEditing ?? false,
});

export const SubDivisionForm: React.FC<SubDivisionFormProps> = ({
  mode,
  initialValues,
  onSubmit,
  loading,
  onGoBack,
  supervisors = [],
  builderSupervisors = [],
  supervisorsLoading = false,
  selectedSupervisors = [],
  selectedBuilderSupervisors = [],
  onSupervisorsChange,
  onBuilderSupervisorsChange,
  supervisorError,
  selectedRegion,
  regionLoading,
  onRegionChange,
  canToggleBumpoutTimesheet,
  canViewGeofencingThreshold,
  canEditGeofencingThreshold,
}) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const { notify } = SnackbarContainer.useContainer();
  const isEditMode = mode === 'edit';

  // Stabilize with id-based key so new object references from Redux/useGetOne
  // don't trigger react-hook-form's internal reset while the user is editing.
  const formValues = useMemo(
    () =>
      initialValues
        ? getDefaultValues(
            initialValues,
            selectedRegion?.phaseStructureType,
            mode,
          )
        : undefined,
    // eslint-disable-next-line
    [
      initialValues?.id,
      initialValues?.geoDistanceTolerance,
      selectedRegion?.phaseStructureType,
      mode,
    ],
  );

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SubDivisionFormValues, SubdivisionValidationContext>({
    defaultValues: getDefaultValues(
      initialValues,
      selectedRegion?.phaseStructureType,
      mode,
    ),
    values: formValues,
    resetOptions: { keepDirtyValues: true },
    resolver: createValidationResolver<
      SubDivisionFormValues,
      SubdivisionValidationContext
    >(validateSubdivisionCreation),
    context: { region: selectedRegion },
    mode: 'onBlur',
  });

  const phaseStructureType = watch('phaseStructureType');

  // Update phase structure type when region changes (only in create mode)
  useEffect(() => {
    if (!isEditMode && selectedRegion?.phaseStructureType) {
      setValue('phaseStructureType', selectedRegion.phaseStructureType);
    }
  }, [selectedRegion?.phaseStructureType, isEditMode, setValue]);

  // Auto-enable override when phase structure differs from region
  useEffect(() => {
    if (
      selectedRegion &&
      phaseStructureType &&
      phaseStructureType !== selectedRegion.phaseStructureType
    ) {
      setValue('overrideRegionCostPerBoard', true);
    }
  }, [phaseStructureType, selectedRegion, setValue]);

  const loadBuilderOptions = useCallback(
    async (searchTerm: string) => {
      try {
        const response = await dataProvider.getList<BuilderInterface>(
          'builder',
          {
            filter: searchTerm ? { name: searchTerm } : {},
            sort: { field: 'name', order: 'ASC' },
            pagination: { page: 1, perPage: 5 },
          },
        );
        return response.data;
      } catch {
        return [];
      }
    },
    [dataProvider],
  );

  const loadRegionOptions = useCallback(async () => {
    try {
      const response = await dataProvider.getList<RegionDto>('region', {
        filter: {},
        sort: { field: 'name', order: 'ASC' },
        pagination: { page: 1, perPage: Number.MAX_SAFE_INTEGER },
      });
      return response.data;
    } catch {
      return [];
    }
  }, [dataProvider]);

  const [regionOptions, setRegionOptions] = React.useState<RegionDto[]>([]);
  const [builderRefreshKey, setBuilderRefreshKey] = useState(0);

  useEffect(() => {
    loadRegionOptions().then(setRegionOptions);
  }, [loadRegionOptions]);

  // Re-fetch options when toolbar refresh is clicked
  useRefreshTrigger(() => {
    loadRegionOptions().then(setRegionOptions);
    setBuilderRefreshKey(k => k + 1);
  });

  const [selectedBuilder, setSelectedBuilder] =
    React.useState<BuilderInterface | null>(null);

  useEffect(() => {
    const builderId = initialValues?.builderId;
    if (builderId) {
      dataProvider
        .getOne<BuilderInterface>('builder', { id: builderId })
        .then(response => setSelectedBuilder(response.data))
        .catch(() => setSelectedBuilder(null));
    }
  }, [initialValues?.builderId, dataProvider]);

  const onFormSubmit = useMemo(
    () =>
      handleSubmit(
        async data => {
          // Ensure overrideRegionCostPerBoard has a value
          if (!data.overrideRegionCostPerBoard) {
            data.overrideRegionCostPerBoard = false;
          }

          await onSubmit(data);
        },
        () => {
          notify('The form is not valid. Please check for errors', 'error');
        },
      ),
    [handleSubmit, onSubmit, notify],
  );

  const isDrywallAndPaint =
    phaseStructureType === PhaseStructureTypeEnum.DrywallAndPaint;
  const showBumpoutTimesheet = canToggleBumpoutTimesheet && !isDrywallAndPaint;

  return (
    <Box>
      <Box className={classes.formContent}>
        <Box display="flex" mb={2} className="sub-references-inputs">
          <Controller
            name="builderId"
            control={control}
            render={({ field: { onChange } }) => (
              <Autocomplete
                key={`builder-${builderRefreshKey}`}
                label="Builder"
                value={selectedBuilder}
                onChange={builder => {
                  setSelectedBuilder(builder);
                  onChange(builder?.id || '');
                }}
                loadOptions={loadBuilderOptions}
                getOptionLabel={option => option?.name || ''}
                getOptionValue={option => option?.id || ''}
                error={errors.builderId?.message}
                fullWidth
                className={classes.input}
              />
            )}
          />
          <Controller
            name="regionId"
            control={control}
            render={({ field }) => (
              <FormControl
                fullWidth
                className={classes.input}
                variant="filled"
                margin="dense"
                error={!!errors.regionId}
              >
                <InputLabel>Region</InputLabel>
                <Select
                  {...field}
                  onChange={e => {
                    field.onChange(e);
                    const newRegionId = e.target.value as string;
                    if (
                      newRegionId &&
                      newRegionId !== initialValues?.regionId
                    ) {
                      onRegionChange(newRegionId);
                    }
                  }}
                  value={field.value || ''}
                >
                  {regionOptions.map(region => (
                    <MenuItem key={region.id} value={region.id}>
                      {region.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.regionId && (
                  <FormHelperText>{errors.regionId.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
          <Controller
            name="active"
            control={control}
            render={({ field: { onChange, value, ref } }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!value}
                    onChange={e => onChange(e.target.checked)}
                    inputRef={ref}
                    color="primary"
                  />
                }
                label="Active"
              />
            )}
          />
        </Box>

        <Box className={classes.fields} mb={2}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                className={classes.input}
                label="Name"
                error={!!errors.name}
                helperText={errors.name?.message}
                margin="dense"
                variant="filled"
              />
            )}
          />
          {canViewGeofencingThreshold && (
            <Controller
              name="geoDistanceTolerance"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  fullWidth
                  className={classes.input}
                  label="Default Geofencing Threshold (ft)"
                  type="number"
                  variant="filled"
                  margin="dense"
                  disabled={!canEditGeofencingThreshold || loading}
                  error={!!errors.geoDistanceTolerance}
                  helperText={errors.geoDistanceTolerance?.message}
                  inputProps={{ step: 1, min: 100, max: 10000 }}
                  onChange={e =>
                    field.onChange(
                      e.target.value === ''
                        ? null
                        : parseInt(e.target.value, 10),
                    )
                  }
                />
              )}
            />
          )}
        </Box>

        <Box className={classes.fields} mb={2}>
          <Controller
            name="address1"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                className={classes.input}
                label="Address1"
                error={!!errors.address1}
                helperText={errors.address1?.message}
                margin="dense"
                variant="filled"
              />
            )}
          />
          <Controller
            name="address2"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                className={classes.input}
                label="Address2"
                margin="dense"
                variant="filled"
              />
            )}
          />
        </Box>

        <Box className={classes.fields} mb={2}>
          <Controller
            name="city"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                className={classes.input}
                label="City"
                error={!!errors.city}
                helperText={errors.city?.message}
                margin="dense"
                variant="filled"
              />
            )}
          />
          <Controller
            name="zip"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                className={classes.input}
                label="Zip"
                error={!!errors.zip}
                helperText={errors.zip?.message}
                margin="dense"
                variant="filled"
              />
            )}
          />
          <Controller
            name="state"
            control={control}
            render={({ field }) => (
              <FormControl
                fullWidth
                className={classes.input}
                variant="filled"
                margin="dense"
                error={!!errors.state}
              >
                <InputLabel>State</InputLabel>
                <Select {...field} value={field.value || ''}>
                  {states.map(state => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </Select>
                {errors.state && (
                  <FormHelperText>{errors.state.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Box>

        <Box className={classes.flexFields} mb={2}>
          <Controller
            name="warrantyStatus"
            control={control}
            render={({ field }) => (
              <FormControl
                fullWidth
                className={classes.thirdWidth}
                variant="filled"
                margin="dense"
                error={!!errors.warrantyStatus}
              >
                <InputLabel>Warranty Status</InputLabel>
                <Select {...field} value={field.value || ''}>
                  {WarrantyStatusChoices.map(choice => (
                    <MenuItem key={choice.id} value={choice.id}>
                      {choice.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.warrantyStatus && (
                  <FormHelperText>
                    {errors.warrantyStatus.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />

          {isEditMode && (
            <Box mx={2} flex={1}>
              <SubDivisionSupervisorsSection
                supervisors={supervisors}
                builderSupervisors={builderSupervisors}
                selectedSupervisors={selectedSupervisors}
                selectedBuilderSupervisors={selectedBuilderSupervisors}
                onSupervisorsChange={onSupervisorsChange}
                onBuilderSupervisorsChange={onBuilderSupervisorsChange}
                supervisorError={supervisorError}
                disabled={supervisorsLoading || regionLoading}
                loading={supervisorsLoading || regionLoading}
              />
            </Box>
          )}
        </Box>

        {selectedRegion && (
          <SubDivisionCostStructureFields
            control={control}
            watch={watch}
            errors={errors}
            disabled={loading || regionLoading}
            isEditMode={isEditMode}
            regionPhaseStructureType={selectedRegion?.phaseStructureType}
            subdivisionId={initialValues?.id}
          />
        )}

        {showBumpoutTimesheet && (
          <BumpoutTimesheetField
            control={control}
            watch={watch}
            initialValue={initialValues?.reqBumpoutTimesheet ?? false}
            disabled={loading}
          />
        )}
      </Box>

      <FormToolbar
        onGoBack={onGoBack}
        onSave={onFormSubmit}
        saveLoading={loading}
        showSave={true}
      />
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  formContent: {
    padding: theme.spacing(2),
  },
  input: {
    margin: '0px 15px',
  },
  fields: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  flexFields: {
    width: '100%',
    display: 'flex',
  },
  thirdWidth: {
    width: '30%',
    margin: '0px 15px',
  },
}));

export default SubDivisionForm;
